import { createAsyncThunk } from '@reduxjs/toolkit';

import { mapTranscriptToRecognizedText } from '../../../../learn/mappers/transcript-to-recognized-text.ts';
import { selectCurrentLessonCharacter } from '../../../../learn/selectors/current-lesson-character.selector.ts';
import { setBallIsVisible } from '../../../../learn/slices/ball/ball.slice.ts';
import { TrackingEventEnum } from '../../../../main/constants/tracking-event.ts';
import { ThunkApiConfigType } from '../../../../main/store/main.store.ts';
import { getAudioPlayerUtil } from '../../../../main/utils/audio/audio-player.util.ts';
import { getSpeechRecognitionUtil } from '../../../../main/utils/audio/speech-recognition.util.ts';
import { getLocaleUtil } from '../../../../main/utils/locale.util.ts';

import { FEEDBACK_SLICE_NAME } from '../feedback.slice.types.ts';

import { sendFeedbackThunk } from './send-feedback.thunk.ts';

const THUNK_NAME = `${FEEDBACK_SLICE_NAME}/collectLessonEndFeedback`;

export const collectLessonEndFeedbackThunk = createAsyncThunk<void, void, ThunkApiConfigType>(
  THUNK_NAME,
  async (_, thunkAPI) => {
    const state = thunkAPI.getState();
    const character = selectCurrentLessonCharacter(state);

    const questions = [
      getLocaleUtil().select('feedback:lesson_end_feedback_enjoyment_question'),
      getLocaleUtil().select('feedback:lesson_end_feedback_difficulty_question'),
      getLocaleUtil().select('feedback:lesson_end_feedback_other_question'),
    ];
    const responses: string[] = [];

    const audioPlayerUtil = getAudioPlayerUtil();
    const speechRecognitionUtil = getSpeechRecognitionUtil();

    for (const question of questions) {
      audioPlayerUtil.speak(question, character);
      await audioPlayerUtil.waitSpeakCompletion();

      thunkAPI.dispatch(setBallIsVisible(true));
      const result = await speechRecognitionUtil.recognizeSpeech({
        silenceDetection: {
          enabled: true,
        },
      });
      thunkAPI.dispatch(setBallIsVisible(false));

      if (!result.success || !result.isFinal) {
        return;
      }

      const recognizedText = mapTranscriptToRecognizedText(result.transcript);

      responses.push(recognizedText);
    }

    thunkAPI.dispatch(
      sendFeedbackThunk({
        event: TrackingEventEnum.FEEDBACK_PROVIDED_LESSON_END,
        questions,
        responses,
      })
    );

    const response = getLocaleUtil().select('feedback:lesson_end_feedback_response');

    audioPlayerUtil.speak(response, character);
    await audioPlayerUtil.waitSpeakCompletion();
  }
);
